// Minimal, real bit-encoded TCF v2.2 Core segment (parameterized).
// Purpose: unblock __tcfapi('getTCData') with a true Core segment bitstring.
class BW {
    bits = [];
    writeN(v, n) {
        for (let i = n - 1; i >= 0; i--)
            this.bits.push((v >> i) & 1);
    }
    writeB(b) {
        this.bits.push(b ? 1 : 0);
    }
    writeBits(arr) {
        this.bits.push(...arr);
    }
    toB64u() {
        const bytes = [];
        for (let i = 0; i < this.bits.length; i += 8) {
            let byte = 0;
            for (let j = 0; j < 8 && i + j < this.bits.length; j++)
                byte = (byte << 1) | this.bits[i + j];
            if (i + 8 > this.bits.length)
                byte <<= 8 - (this.bits.length - i);
            bytes.push(byte);
        }
        return Buffer.from(bytes)
            .toString('base64')
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }
}
class BR {
    bits = [];
    i = 0;
    constructor(b64u) {
        const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
        const buf = Buffer.from(b64 + '==='.slice((b64.length + 3) % 4), 'base64');
        for (const byte of buf)
            for (let b = 7; b >= 0; b--)
                this.bits.push((byte >> b) & 1);
    }
    readN(n) {
        let v = 0;
        for (let k = 0; k < n; k++)
            v = (v << 1) | this.bits[this.i++];
        return v;
    }
    readB() {
        return this.readN(1) === 1;
    }
}
function writeBoolMap(w, ids, on) {
    for (const id of ids)
        w.writeB(on(id));
}
function readBoolSet(r, ids) {
    const set = new Set();
    for (const id of ids)
        if (r.readB())
            set.add(id);
    return set;
}
export function encodeCore(model, opts) {
    const w = new BW();
    // Internal core version marker (2 bits) = 1
    w.writeN(1, 2);
    // Timestamps (epoch seconds)
    const createdSec = Math.floor(model.created / 1000);
    const updatedSec = Math.floor(model.lastUpdated / 1000);
    w.writeN(createdSec, 36);
    w.writeN(updatedSec, 36);
    // CMP + policy
    w.writeN(model.cmpId, 12);
    w.writeN(model.cmpVersion, 12);
    w.writeN(model.policyVersion, 6);
    w.writeB(model.gdprApplies);
    // Purposes (1..12): consent then LI
    const pIds = Array.from({ length: 12 }, (_, i) => i + 1);
    writeBoolMap(w, pIds, (id) => !!model.purposeConsents[id]);
    writeBoolMap(w, pIds, (id) => !!model.purposeLI[id]);
    // Special features (1..2)
    const sfIds = [1, 2];
    writeBoolMap(w, sfIds, (id) => !!model.specialFeatures[id]);
    // Vendors — flat bitmaps up to maxVendorId
    const vIds = Array.from({ length: opts.maxVendorId }, (_, i) => i + 1);
    writeBoolMap(w, vIds, (id) => model.vendorConsents.has(id));
    writeBoolMap(w, vIds, (id) => model.vendorLI.has(id));
    return w.toB64u();
}
export function decodeCore(b64u, maxVendorId) {
    const r = new BR(b64u);
    const ver = r.readN(2);
    if (ver !== 1)
        throw new Error(`Unsupported core version ${ver}`);
    const created = r.readN(36) * 1000;
    const lastUpdated = r.readN(36) * 1000;
    const cmpId = r.readN(12);
    const cmpVersion = r.readN(12);
    const policyVersion = r.readN(6);
    const gdprApplies = r.readB();
    const pIds = Array.from({ length: 12 }, (_, i) => i + 1);
    const purposeConsentsMutable = {};
    const purposeLIMutable = {};
    for (const id of pIds)
        purposeConsentsMutable[id] = r.readB();
    for (const id of pIds)
        purposeLIMutable[id] = r.readB();
    const specialFeatures = { 1: false, 2: false };
    for (const id of [1, 2])
        specialFeatures[id] = r.readB();
    const vIds = Array.from({ length: maxVendorId }, (_, i) => i + 1);
    const vendorConsents = readBoolSet(r, vIds);
    const vendorLI = readBoolSet(r, vIds);
    return {
        created,
        lastUpdated,
        cmpId,
        cmpVersion,
        policyVersion,
        gdprApplies,
        purposeConsents: purposeConsentsMutable,
        purposeLI: purposeLIMutable,
        specialFeatures,
        vendorConsents,
        vendorLI,
    };
}
export function coreToTCData(model) {
    return {
        tcfPolicyVersion: model.policyVersion,
        cmpId: model.cmpId,
        cmpVersion: model.cmpVersion,
        gdprApplies: model.gdprApplies,
        purpose: {
            consents: model.purposeConsents,
            legitimateInterests: model.purposeLI,
        },
        specialFeatureOptins: model.specialFeatures,
        vendor: {
            consentsCount: model.vendorConsents.size,
            legitimateInterestsCount: model.vendorLI.size,
        },
    };
}
//# sourceMappingURL=core.js.map